import { action } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { uniqBy } from '@ember/object/computed';
import Component from '@glimmer/component';
import type SkinnyLocationModel from 'garaje/models/skinny-location';

const DEFAULT_PAGE_SIZE = 25;

interface GlobalDevicesIpadsFiltersComponentSignature {
  Args: {
    locationId: string;
    changeLocationId: (locationId: string) => void;
    changeStatus: (status: string) => void;
    wifiNetwork: string;
    changeWifiNetwork: (wifiNetwork: string) => void;
    wifiNetworks: string[];
    deviceType: string;
    changeDeviceType: (deviceType: string) => void;
    deviceTypes: string[];
    locations: SkinnyLocationModel[];
    pageNumber: number;
    pageSize: number;
    statuses: string[];
    totalRecords: number;
    setPageNumber: (pageNumber: number) => void;
  };
}

export default class GlobalDevicesIpadsFiltersComponent extends Component<GlobalDevicesIpadsFiltersComponentSignature> {
  /*
   * Filters
   */
  defaultWifiNetworkFilter = 'All Wi-Fi networks';
  defaultDeviceType = 'All iPad types';
  defaultStatus = 'All statuses';
  @uniqBy('args.locations', 'id') uniqLocations!: SkinnyLocationModel[];
  defaultLocationFilter = { name: 'All locations', id: '' };

  /*
   * Defaults
   */
  get wifiNetworks(): string[] {
    return this.args.wifiNetworks ?? [];
  }
  get deviceTypes(): string[] {
    return this.args.deviceTypes ?? [];
  }
  get pageNumber(): number {
    return this.args.pageNumber ?? 1;
  }
  get pageSize(): number {
    return this.args.pageSize ?? DEFAULT_PAGE_SIZE;
  }
  get totalRecords(): number {
    return this.args.totalRecords ?? 0;
  }
  get statuses(): string[] {
    return this.args.statuses ?? ['operational', 'offline'];
  }

  get wifiNetworkOptions(): string[] {
    return [this.defaultWifiNetworkFilter, ...this.wifiNetworks];
  }

  get deviceTypeOptions(): string[] {
    return [this.defaultDeviceType, ...this.deviceTypes];
  }

  get locationOptions(): [Partial<SkinnyLocationModel>, ...SkinnyLocationModel[]] {
    return [this.defaultLocationFilter, ...this.uniqLocations];
  }

  get statusOptions(): string[] {
    return [this.defaultStatus, ...this.statuses];
  }

  /*
   * Actions
   */
  changeIfNotDefault(newValue: string, defaultValue: string, changeFunction: (value: string) => void): void {
    // If it's the default vaulue, we want to remove the filter
    if (newValue === defaultValue) {
      changeFunction('');
    } else {
      changeFunction(newValue);
    }
  }

  @action
  onChangeWifiNetwork(newValue: string): void {
    this.changeIfNotDefault(newValue, this.defaultWifiNetworkFilter, this.args.changeWifiNetwork);
  }

  @action
  onChangeDeviceType(newValue: string): void {
    this.changeIfNotDefault(newValue, this.defaultDeviceType, this.args.changeDeviceType);
  }

  @action
  onChangeStatus(newValue: string): void {
    this.changeIfNotDefault(newValue, this.defaultStatus, this.args.changeStatus);
  }
}
